import { useCallback, useState } from "react";
import { api } from "@/services/api";
import type { StripePaymentIntentResponse, StripePaymentStatusResponse } from "@/types/api";
import { logError } from "@/utils/logError";

interface UseStripePaymentReturn {
  loading: boolean;
  createPaymentIntent: (amount: number, bookingId: string) => Promise<StripePaymentIntentResponse>;
  checkPaymentStatus: (
    bookingId: string,
    paymentIntent: string,
  ) => Promise<StripePaymentStatusResponse>;
}

export function useStripePayment(): UseStripePaymentReturn {
  const [loading, setLoading] = useState(false);

  const createPaymentIntent = useCallback(
    async (amount: number, bookingId: string): Promise<StripePaymentIntentResponse> => {
      setLoading(true);

      try {
        const response = await api.createStripePaymentIntent(amount, bookingId);
        setLoading(false);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setLoading(false);
        throw error;
      }
    },
    [],
  );

  const checkPaymentStatus = useCallback(
    async (bookingId: string, paymentIntent: string): Promise<StripePaymentStatusResponse> => {
      try {
        const response = await api.getStripePaymentStatus(bookingId, paymentIntent);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        throw error;
      }
    },
    [],
  );

  return {
    loading,
    createPaymentIntent,
    checkPaymentStatus,
  };
}
